import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

const SORT_OPTIONS = [
  { value: 'latest', label: '最新更新' },
  { value: 'popular', label: '最受欢迎' },
  { value: 'rating', label: '评分最高' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: '全部状态' },
  { value: '连载中', label: '连载中' },
  { value: '已完结', label: '已完结' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('latest');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setCurrentPage(1);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  let filteredComics: Comic[] = [];

  if (slug === 'all' || !slug || slug === 'recommended') {
    filteredComics = [...comics];
    if (slug === 'recommended') {
      filteredComics = filteredComics.filter((c) => c.isHot || c.isRecommended);
    }
  } else {
    filteredComics = comics.filter((c) => c.category === slug);
  }

  if (statusFilter === '连载中') {
    filteredComics = filteredComics.filter((c) => c.status === '连载中');
  } else if (statusFilter === '已完结') {
    filteredComics = filteredComics.filter((c) => c.status === '已完结');
  }

  switch (sortBy) {
    case 'popular':
      filteredComics.sort((a, b) => b.views - a.views);
      break;
    case 'rating':
      filteredComics.sort((a, b) => b.rating - a.rating);
      break;
    case 'latest':
    default:
      filteredComics.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      break;
  }

  const totalPages = Math.ceil(filteredComics.length / itemsPerPage);
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.label },
  ];

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.label} - 韩国漫画网`,
    description: `浏览${currentCategory.label}分类下的韩国漫画，韩国漫画网提供免费的韩漫在线观看和下拉式阅读体验。`,
    url: `https://koreanmanhwa.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画网',
      url: 'https://koreanmanhwa.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <title>{currentCategory.label} - 韩国漫画网</title>
      <meta name="description" content={`浏览${currentCategory.label}分类下的韩国漫画，韩国漫画网提供免费的韩漫在线观看和下拉式阅读体验。海量韩漫每日更新，完全免费。`} />
      <meta name="keywords" content={`韩国漫画网, 韩漫在线观看, ${currentCategory.label}, 免费韩漫阅读`} />
      <link rel="canonical" href={`https://koreanmanhwa.com/category/${slug}`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {currentCategory.label}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredComics.length} 部漫画作品
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className={`px-4 py-1.5 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    (slug === cat.slug || (slug === 'recommended' && cat.slug === 'recommended') || (!slug && cat.slug === 'all'))
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.label}
                </Link>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4 mb-8">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                <div className="flex gap-1">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setSortBy(opt.value); setCurrentPage(1); }}
                      className={`px-3 py-1 text-xs rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                        sortBy === opt.value
                          ? 'bg-secondary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">状态：</span>
                <div className="flex gap-1">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setStatusFilter(opt.value); setCurrentPage(1); }}
                      className={`px-3 py-1 text-xs rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                        statusFilter === opt.value
                          ? 'bg-secondary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {paginatedComics.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                  {paginatedComics.map((comic) => (
                    <div key={comic.id} className="flex justify-center">
                      <ComicCard comic={comic} size="small" />
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer ${
                          currentPage === page
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
                  <i className="ri-book-open-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无漫画</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 px-5 py-2 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览全部漫画
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}